import { ChevronDown } from "lucide-react";
import type { SelectHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Trailing chevron for controls that open a list.
 * Same icon, size, stroke, and color as the Country field
 * (14px Lucide chevron, slate-800 at 60% opacity).
 */
const crmListChevronClassName =
  "h-3.5 w-3.5 shrink-0 text-slate-800 opacity-60 dark:text-slate-100";

export function CrmListChevron({ className }: { className?: string }) {
  return <ChevronDown className={cn(crmListChevronClassName, className)} aria-hidden />;
}

/** Border chrome shared with the Country button. Padding stays off this string so the chevron inset can match. */
export const crmSelectFieldWrapperClass =
  "rounded-md border border-gray-300 dark:border-white/20 dark:bg-black/20";

type CrmSelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  /**
   * Width, border, and background for the outer box.
   * The chevron is inset inside that box, 12px from its trailing border.
   */
  wrapperClassName?: string;
  /**
   * `field` fills the outer box (address type, state, social).
   * `inline` hugs the label (email type) so the chevron stays inside the segment.
   */
  layout?: "field" | "inline";
};

/**
 * Native select whose down arrow matches the Country field chevron.
 * The icon sits in the flow of a grid cell, 12px inside the trailing border
 * (`pr-3`), the same inset as Country's `px-3`. `pr-9` keeps the label clear of the icon.
 */
export function CrmSelect({
  className,
  wrapperClassName,
  layout = "field",
  children,
  disabled,
  ...props
}: CrmSelectProps) {
  const inline = layout === "inline";
  return (
    <div
      className={cn(
        "grid",
        inline ? "w-max max-w-[9.5rem] shrink-0" : "w-full min-w-0",
        disabled && "opacity-50",
        wrapperClassName,
      )}
    >
      <select
        disabled={disabled}
        className={cn(
          "col-start-1 row-start-1 appearance-none bg-transparent bg-none outline-none",
          "[-moz-appearance:none] [-webkit-appearance:none]",
          inline ? "w-max max-w-full" : "w-full min-w-0",
          className,
          "pr-9",
        )}
        {...props}
      >
        {children}
      </select>
      <span className="pointer-events-none col-start-1 row-start-1 flex items-center self-center justify-self-end pr-3">
        <CrmListChevron />
      </span>
    </div>
  );
}
